"use client";

import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheBrunchMenu() {
  const {
    ref: openerRef,
    isVisible: openerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const courses = [{
    id: "morning-bites",
    tag: "Nº 01",
    label: "MORNING BITES",
    title: "Morning — Light Beginnings",
    caption: "— DELICATE STARTERS FOR A GENTLE START",
    items: [{
      name: "Seasonal Fruit Parfait",
      desc: "Organic Greek yogurt, house-made honey granola, and fresh berries from the local market.",
      price: "14"
    }, {
      name: "Avocado Sourdough",
      desc: "Crushed Hass avocado, micro-greens, radish, and a drizzle of cold-pressed olive oil.",
      price: "16"
    }, {
      name: "Miniature Quiche",
      desc: "Flaky pastry crust filled with baby spinach, gruyère cheese, and farm-fresh eggs.",
      price: "12"
    }, {
      name: "Smoked Salmon Blinis",
      desc: "Buckwheat blinis topped with crème fraîche, capers, and sustainably sourced salmon.",
      price: "18"
    }]
  }, {
    id: "main-brunch",
    tag: "Nº 02",
    label: "MAIN BRUNCH",
    title: "Brunch — The Heart",
    caption: "— SUBSTANTIAL PLATES TO CELEBRATE",
    items: [{
      name: "Brioche French Toast",
      desc: "Thick-cut brioche soaked in vanilla bean custard, served with pure maple syrup.",
      price: "22"
    }, {
      name: "Garden Herb Frittata",
      desc: "Slow-baked eggs with fresh dill, parsley, and chives, served with a side of baby arugula.",
      price: "20"
    }, {
      name: "Eggs Benedict",
      desc: "Poached farm eggs, Canadian bacon, and silky hollandaise on a toasted English muffin.",
      price: "24"
    }, {
      name: "Wild Mushroom Crepes",
      desc: "Savory crepes filled with sautéed forest mushrooms and a light béchamel sauce.",
      price: "21"
    }, {
      name: "Lemon Ricotta Pancakes",
      desc: "Fluffy pancakes with whipped ricotta and a hint of lemon zest, topped with blueberry compote.",
      price: "19"
    }]
  }, {
    id: "savory-sides",
    tag: "Nº 03",
    label: "SAVORY SIDES",
    title: "Sides — Accompaniments",
    caption: "— PERFECT PAIRINGS FOR THE TABLE",
    items: [{
      name: "Maple Glazed Bacon",
      desc: "Thick-cut smoked bacon brushed with organic maple syrup and cracked pepper.",
      price: "8"
    }, {
      name: "Truffle Fingerling Potatoes",
      desc: "Roasted potatoes tossed in white truffle oil and fresh rosemary.",
      price: "10"
    }, {
      name: "Heirloom Tomato Salad",
      desc: "Sliced tomatoes with sea salt, basil, and a light balsamic reduction.",
      price: "9"
    }, {
      name: "Artisanal Bread Basket",
      desc: "A selection of warm sourdough, croissants, and muffins with cultured butter.",
      price: "12"
    }]
  }, {
    id: "sweet-treats",
    tag: "Nº 04",
    label: "SWEET TREATS",
    title: "Sweet — The Celebration",
    caption: "— A FINAL NOTE OF JOY AND INDULGENCE",
    items: [{
      name: "Baby Ohlson Cupcakes",
      desc: "Vanilla bean sponge with a soft sage-colored buttercream frosting.",
      price: "6"
    }, {
      name: "Lavender Macarons",
      desc: "Delicate almond shells with a honey and lavender ganache filling.",
      price: "4"
    }, {
      name: "Petite Lemon Tarts",
      desc: "Shortbread crust with zesty lemon curd and a single fresh raspberry.",
      price: "8"
    }, {
      name: "Sparkling Mimosa Float",
      desc: "Orange sorbet topped with chilled non-alcoholic sparkling cider.",
      price: "10"
    }]
  }];
  const [activeCourse, setActiveCourse] = useState<string>("morning-bites");
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute("data-course-id");
          if (id) setActiveCourse(id);
        }
      });
    }, {
      rootMargin: "-30% 0px -60% 0px",
      threshold: 0
    });
    Object.values(sectionRefs.current).forEach(el => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  const handleCourseClick = (id: string) => {
    const el = sectionRefs.current[id];
    if (el) {
      const yOffset = -120;
      const y = el.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({
        top: y,
        behavior: "smooth"
      });
    }
  };
  const PastaCurl = () => <svg width="48" height="20" viewBox="0 0 48 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-border" aria-hidden="true">
      <path d="M2 10 C 6 4, 12 16, 18 10 C 24 4, 30 16, 36 10 C 40 6, 44 12, 46 10" stroke="currentColor" strokeWidth="1" strokeLinecap="round" fill="none" />
    </svg>;
  return <section data-section-id="1020" id="the-brunch-menu" className="relative bg-background py-24 md:py-32 lg:py-44 overflow-hidden dark bg-background text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "radial-gradient(circle at 25% 30%, hsl(145 30% 25%) 0.5px, transparent 1px), radial-gradient(circle at 75% 70%, hsl(145 30% 25%) 0.5px, transparent 1px)",
      backgroundSize: "32px 32px, 48px 48px"
    }} aria-hidden="true" />

      {}
      <div className="lg:hidden sticky top-16 z-30 -mx-6 mb-12 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex items-center gap-6 px-6 py-3 whitespace-nowrap">
            {courses.map((c, i) => <Button key={c.id} data-index={i} onClick={() => handleCourseClick(c.id)} variant="ghost" className={`font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-240 h-auto px-0 py-0 rounded-md ${activeCourse === c.id ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                <span className="flex items-center gap-2">
                  {activeCourse === c.id && <span className="inline-block h-1.5 w-1.5 rounded-md bg-primary" />}
                  {c.label}
                </span>
              </Button>)}
          </div>
        </div>
      </div>

      <div className="relative mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {}
          <div className="lg:col-span-9">
            {}
            <div ref={openerRef} className="relative mb-20 md:mb-28">
              {}
              <div aria-hidden="true" className="pointer-events-none absolute -top-8 -left-4 md:-top-16 md:-left-8 select-none font-serif text-[12rem] md:text-[22rem] leading-none font-light text-primary/[0.05]" style={{
              WebkitTextStroke: "1px currentColor",
              color: "transparent"
            }}>
                O
              </div>

              <div className={`relative transition-all duration-700 ease-out ${openerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
              transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)"
            }}>
                <div className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-primary mb-6 md:mb-8">
                  — Nº 01 · THE BRUNCH
                </div>

                <h1 className="font-default font-medium text-foreground leading-[1.1] tracking-tighter" style={{
                fontSize: "clamp(2.5rem, 8vw, 5rem)"
              }}>
                  A Celebration of{" "}
                  <span className={`italic text-primary inline-block transition-all duration-600 ${openerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`} style={{
                  transitionDelay: "200ms"
                }}>
                    New Life
                  </span>
                </h1>

                <p className="mt-8 md:mt-10 max-w-[52ch] text-muted-foreground font-default font-normal leading-[1.6] text-lg md:text-xl">
                  Our brunch menu is a curated collection of seasonal flavors, designed to be shared among family and friends as we welcome Baby Ohlson. Each dish reflects the warmth and joy of this new chapter, prepared with organic ingredients and a touch of modern elegance.
                </p>
              </div>
            </div>

            {}
            {courses.map((course, idx) => <div key={course.id} data-index={idx} data-course-id={course.id} ref={el => {
            sectionRefs.current[course.id] = el;
          }} className="scroll-mt-32">
                {}
                <div className="mb-6">
                  <span className="inline-block font-mono text-xs tracking-[0.2em] uppercase text-primary border border-border px-3 py-1 rounded-md">
                    {course.tag}
                  </span>
                </div>

                {}
                <h2 className="font-default font-normal text-foreground leading-[1.2] tracking-tight" style={{
              fontSize: "clamp(2rem, 5vw, 3.5rem)"
            }}>
                  {course.title}
                </h2>

                <p className="mt-4 font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-muted-foreground">
                  {course.caption}
                </p>

                {}
                <ul className="mt-10 md:mt-14">
                  {course.items.map((item, i) => <li key={i} data-index={i} className="group border-b border-dotted border-border py-6 px-2 -mx-2 transition-colors duration-240 ease-out hover:bg-card cursor-default rounded-md">
                      <div className="flex items-baseline gap-3 md:gap-4">
                        <h3 className="font-default font-semibold text-foreground text-xl md:text-2xl leading-tight tracking-tight shrink-0">
                          {item.name}
                        </h3>
                        <span aria-hidden="true" className="flex-1 border-b border-dotted border-border group-hover:border-solid group-hover:border-primary transition-all duration-240 translate-y-[-4px]" />
                        <span className="font-mono text-base md:text-lg font-medium tracking-[0.05em] text-primary shrink-0">
                          ${item.price}
                        </span>
                      </div>
                      <p className="mt-2 font-default text-[0.95rem] leading-[1.6] text-muted-foreground max-w-[58ch]">
                        {item.desc}
                      </p>
                    </li>)}
                </ul>

                {}
                {idx < courses.length - 1 && <div className="flex justify-center py-16 md:py-20">
                    <PastaCurl />
                  </div>}
              </div>)}

            {}
            <div className="mt-20 md:mt-28 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-8 py-6 font-default font-medium tracking-[0.08em] uppercase text-sm shadow-md" size="lg">
                View Gift Registry
              </Button>
              <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">
                PLEASE RSVP BY OCTOBER 15TH
              </p>
            </div>
          </div>

          {}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-32">
              <div className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground mb-8">
                — Nº · MENU INDEX
              </div>

              <div className="relative pl-6">
                {}
                <span aria-hidden="true" className="absolute left-0 top-2 bottom-2 w-px bg-border" />

                <ul className="flex flex-col gap-6">
                  {courses.map((c, i) => {
                  const isActive = activeCourse === c.id;
                  return <li key={c.id} data-index={i} className="relative">
                        {isActive && <span aria-hidden="true" className="absolute -left-6 top-1/2 -translate-y-1/2 h-2 w-2 rounded-md bg-primary" />}
                        <Button onClick={() => handleCourseClick(c.id)} variant="ghost" className={`h-auto px-0 py-0 text-left font-mono text-sm tracking-[0.2em] uppercase transition-colors duration-240 rounded-md ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                          {c.label}
                        </Button>
                      </li>;
                })}
                </ul>
              </div>

              {}
              <div className="mt-12 pt-8 border-t border-dotted border-border">
                <p className="font-serif italic text-foreground text-base leading-relaxed">
                  "A morning of <span className="text-primary">joy</span>."
                </p>
                <p className="mt-3 font-mono text-[0.7rem] tracking-[0.2em] uppercase text-muted-foreground">
                  — WELCOME BABY OHLSON
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}